import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { LegalDocId, legalDocs } from '../data/legal';
type LegalModalProps = {
  openDoc: LegalDocId | null;
  onChange: (doc: LegalDocId) => void;
  onClose: () => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function LegalModal({
  openDoc,
  onChange,
  onClose
}: LegalModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const doc = legalDocs.find(d => d.id === openDoc);
  useEffect(() => {
    if (!openDoc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [openDoc, onClose]);
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: 0
    });
  }, [openDoc]);
  return <AnimatePresence data-id="element-197" data-mp="1fnoqp1ydb3jn">
      {doc && <motion.div className="fixed inset-0 z-[1200] flex items-end justify-center p-0 sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-198" data-mp="1komj07d3ddyv">
          <div className="absolute inset-0 bg-forest-dark/60" onClick={onClose} aria-hidden="true" data-id="element-199" data-mp="s1s1njbkmupm" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="legal-title" className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-200" data-mp="wzbd6bgvwtdv">
            <div className="flex items-center justify-between gap-4 border-b border-stone-line px-6 py-4" data-id="element-201" data-mp="669slkph3aah">
              <div role="tablist" aria-label="Legal documents" className="flex gap-1 rounded-lg bg-stone-soft p-1" data-id="element-202" data-mp="18hug8y8irxu">
                {legalDocs.map(d => {
              const active = d.id === doc.id;
              return <button key={d.id} role="tab" aria-selected={active} onClick={() => onChange(d.id)} className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${active ? 'bg-white text-forest shadow-sm' : 'text-ink-muted hover:text-ink'}`} data-id="element-203" data-mp="b0ntu3qtgbn2">
                      {d.id === 'privacy' ? 'Privacy' : 'Terms'}
                    </button>;
            })}
              </div>
              <button ref={closeRef} onClick={onClose} aria-label="Close" className="flex h-9 w-9 items-center justify-center rounded-md text-ink-muted transition-colors duration-150 hover:bg-stone-soft hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-forest" data-id="element-204" data-mp="18mbfpupnbbkc">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-205" data-mp="1b93zepbb0nu7" />
              </button>
            </div>

            <div ref={scrollRef} className="overflow-y-auto px-6 py-7 md:px-8" data-id="element-206" data-mp="1262x6zpk7gvt">
              <h2 id="legal-title" className="font-display text-3xl font-bold text-forest" data-id="element-207" data-mp="1285y4es92ufb">
                {doc.title}
              </h2>
              <p className="mt-1 text-sm text-ink-muted" data-id="element-208" data-mp="1twqxk9jesi8f">Last updated {doc.updated}</p>
              <div className="mt-7 space-y-6" data-id="element-209" data-mp="1yw2h1qb5u1tp">
                {doc.sections.map(s => <section key={s.heading} data-id="element-210" data-mp="1yyg71kqzgnue">
                    <h3 className="font-semibold text-ink" data-id="element-211" data-mp="1l5qltija3myc">{s.heading}</h3>
                    <p className="mt-1.5 leading-relaxed text-ink-muted" data-id="element-212" data-mp="1p6iv2bs5hrum">{s.body}</p>
                  </section>)}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}